import type { ComponentProps, ReactNode } from 'react'
import { colClass, type ColSpan } from '@/components/console/classes'
import { cn } from '@/lib/utils'

/** One console screen (`section.screen.on`). Every page under /dashboard renders one. */
export function Screen({ className, children, ...props }: ComponentProps<'section'>) {
  return (
    <section className={cn('screen on', className)} {...props}>
      {children}
    </section>
  )
}

interface PageHeadProps {
  /** The screen's h2, e.g. "Everything's answered." The header's h1 names the screen. */
  title: ReactNode
  /** The `.psub` line under it. */
  subtitle?: ReactNode
  /** Right-aligned after the spacer: range selects, the primary action. */
  actions?: ReactNode
  className?: string
}

/** The page intro (`.phead`): h2 and sub-line on the left, actions on the right. */
export function PageHead({ title, subtitle, actions, className }: PageHeadProps) {
  return (
    <div className={cn('phead', className)}>
      <div>
        <h2>{title}</h2>
        {subtitle ? <p className="psub">{subtitle}</p> : null}
      </div>
      {actions ? (
        <>
          <div className="sp" />
          {actions}
        </>
      ) : null}
    </div>
  )
}

/**
 * The prototype's 12-column grid (`.grid` there, `.cgrid` here: Tailwind owns
 * `grid`). 20px gaps, 14px under 680px.
 */
export function Grid({ className, children, ...props }: ComponentProps<'div'>) {
  return (
    <div className={cn('cgrid', className)} {...props}>
      {children}
    </div>
  )
}

/** A grid cell. Usually you want `<Card span={4}>` instead. */
export function Col({ span, className, children, ...props }: ComponentProps<'div'> & { span: ColSpan }) {
  return (
    <div className={cn(colClass(span), className)} {...props}>
      {children}
    </div>
  )
}

/** The flexible spacer (`.sp`) that pushes what follows to the right. */
export function Spacer() {
  return <div className="sp" />
}
